import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback, useState } from "react";

import type { UserListItemDto } from "@/api/types";
import { convertToCsv, downloadCsvAsExcel } from "@/components/DataTable/sheet";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { sanitizeSpreadsheetCell } from "@/helpers/spreadsheet";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { canAssignRoles } from "@/modules/users/permissions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

type UserExportModalProps = ModalBaseProps & {
  query: ApiQueryParams<"getUsersV1">;
};

type UserExportType = "all" | "onboarded" | "invited";

/**
 * Exports neighbours matching the active users list query, including the current search, tab, filters, and sorting.
 */
export function UserExportModal({ isOpened, onOpenChange, query }: UserExportModalProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const showFlashToast = useFlashToast();
  const hasPermission = usePermission();
  const [downloadingType, setDownloadingType] = useState<UserExportType | null>(null);
  const canExportRoles = sessionUser.isSuperAdmin || hasPermission(canAssignRoles);

  const userQueries = useUserQueries();
  const { data: users = [], isFetching: isFetchingUsers } = useQuery({
    ...userQueries.exportList(query),
    enabled: isOpened,
  });

  const permissionQueries = usePermissionQueries();
  const { data: roles = [], isFetching: isFetchingRoles } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: isOpened && canExportRoles,
  });
  const isFetchingExportData = isFetchingUsers || (canExportRoles && isFetchingRoles);

  const handleDownload = useCallback(
    async (type: UserExportType) => {
      setDownloadingType(type);

      try {
        const filteredUsers = filterUsers(users, type);
        const csv = await convertToCsv(
          filteredUsers.map((user) => {
            const row = {
              [t("component.user-table.headers.resident")]: sanitizeSpreadsheetCell(user.fullName),
              [t("component.user-table.headers.email")]: sanitizeSpreadsheetCell(user.email ?? ""),
              [t("page.user-overview.export-modal.column.phone")]: sanitizeSpreadsheetCell(user.phoneNumber ?? ""),
              [sessionUser.project.type === "addressBased"
                ? t("component.user-table.headers.address")
                : t("component.user-table.headers.company")]: sanitizeSpreadsheetCell(user.locatedAt ?? ""),
              [t("component.user-table.data.status")]: getUserStatusLabel(t, user),
              [t("component.user-table.headers.invitation-date")]: user.invitedAt
                ? formatDate(i18n, "date", user.invitedAt)
                : "",
              [t("component.user-table.headers.activation-date")]: user.registeredAt
                ? formatDate(i18n, "date", user.registeredAt)
                : "",
            };

            if (!canExportRoles) {
              return row;
            }

            return {
              ...row,
              [t("page.user-overview.export-modal.column.role")]: sanitizeSpreadsheetCell(
                roles.find((role) => role.id === user.roleId)?.name ?? user.roleId,
              ),
            };
          }),
        );

        await downloadCsvAsExcel(getUserExportFilename(type, sessionUser.project.name), csv);
        postHog.capture("clicked_export_users", { type });
      } catch (error) {
        console.error(error);
        showFlashToast({ variant: "alert", message: t("common.generic-error") });
      } finally {
        setDownloadingType(null);
      }
    },
    [
      canExportRoles,
      i18n,
      postHog,
      roles,
      sessionUser.project.name,
      sessionUser.project.type,
      showFlashToast,
      t,
      users,
    ],
  );

  return (
    <Modal.Root
      title={t("page.user-overview.export-modal.title", {
        count: users.length,
      })}
      description={t("page.user-overview.export-modal.description")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      isPending={isFetchingExportData}
    >
      <Modal.Actions>
        <Button
          variant="outline"
          disabled={isFetchingExportData}
          isLoading={downloadingType === "onboarded"}
          onClick={() => void handleDownload("onboarded")}
        >
          {t("page.user-overview.export-modal.btn.export-onboarded-users")}
        </Button>
        <Button
          variant="outline"
          disabled={isFetchingExportData}
          isLoading={downloadingType === "invited"}
          onClick={() => void handleDownload("invited")}
        >
          {t("page.user-overview.export-modal.btn.export-invited-users")}
        </Button>
        <Button
          disabled={isFetchingExportData}
          isLoading={downloadingType === "all"}
          onClick={() => void handleDownload("all")}
        >
          {t("page.user-overview.export-modal.btn.export-all-users")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

function filterUsers(users: UserListItemDto[], type: UserExportType): UserListItemDto[] {
  if (type === "onboarded") {
    return users.filter((user) => user.registeredAt && !user.deletedAt);
  }

  if (type === "invited") {
    return users.filter((user) => !user.registeredAt && !user.deletedAt);
  }

  return users;
}

function getUserStatusLabel(t: ReturnType<typeof useTranslation>["t"], user: UserListItemDto): string {
  if (user.deletedAt) {
    return t("common.label.user-status.deleted");
  }

  if (user.registeredAt) {
    return t("common.label.user-status.onboarded");
  }

  return t("common.label.user-status.invited");
}

function getUserExportFilename(type: UserExportType, projectName: string): string {
  const date = new Date().toISOString().slice(0, 10);

  return `${type}-users-${projectName}-${date}`;
}
